import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, X as XIcon, DownloadSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const fmtBytes = (n) => {
  const v = Number(n) || 0;
  if (v < 1024) return v + " B";
  if (v < 1024 * 1024) return (v / 1024).toFixed(1) + " KB";
  return (v / (1024 * 1024)).toFixed(1) + " MB";
};

const PRIORITY_STYLES = {
  high: { bubble: "bg-[#FEF2F2] border-l-[3px] border-[#DC2626]", chip: "bg-[#DC2626] text-white", label: "HIGH" },
  medium: { bubble: "bg-white", chip: "bg-[#F5F5F5] text-[#525252]", label: "MED" },
  low: { bubble: "bg-[#FAFAFA] opacity-80", chip: "bg-[#F5F5F5] text-[#A3A3A3]", label: "LOW" },
};

// Backend stores mentions as "@[Full Name](user:uuid)". For display we want to
// show a small inline chip that reads "@FirstName" only — never the UUID, never
// the full name.
const MENTION_RE = /@\[([^\]]+)\]\(user:[a-zA-Z0-9_\-]+\)/g;
function firstNameOf(fullName) {
  return (fullName || "").trim().split(/\s+/)[0] || fullName;
}
function renderContent(content) {
  const parts = [];
  let last = 0;
  let m;
  while ((m = MENTION_RE.exec(content)) !== null) {
    if (m.index > last) parts.push(content.slice(last, m.index));
    parts.push(
      <span key={m.index} className="inline-block px-1.5 py-[1px] bg-[#EFF6FF] text-[#0055FF] font-semibold rounded-sm text-[12px]" title={m[1]}>
        @{firstNameOf(m[1])}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < content.length) parts.push(content.slice(last));
  return parts.length ? parts : content;
}

// Escape a string for safe use inside a RegExp.
function escRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }

// Before sending, expand each tracked @Firstname in the visible text back into
// the strict "@[Full Name](user:id)" form the backend requires to resolve ids.
// We match tokens in order — duplicates work because each replace only hits the
// leftmost remaining occurrence.
function expandMentions(text, mentionList) {
  let out = text;
  for (const m of mentionList) {
    const re = new RegExp(`@${escRe(m.firstName)}(?![A-Za-z])`);
    if (re.test(out)) {
      out = out.replace(re, `@[${m.fullName}](user:${m.userId})`);
    }
  }
  return out;
}

export default function TaskChatPanel({ taskId, fill = false }) {
  const { user } = useAuth();
  const isInternal = user?.user_type === "internal";
  const [channels, setChannels] = useState([]);
  const [activeChannelId, setActiveChannelId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  // Visible tokens the user has picked via @-autocomplete. Expanded on submit.
  const [pendingMentions, setPendingMentions] = useState([]);
  const [priority, setPriority] = useState("medium");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  // v1.56 — files staged for the next outbound message.
  const [pendingFiles, setPendingFiles] = useState([]);

  // Autocomplete state
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionAnchor, setMentionAnchor] = useState(-1); // position of '@' in textarea
  const textareaRef = useRef(null);
  const listEndRef = useRef(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get(`/tasks/${taskId}/channels`)
      .then(r => {
        if (!live) return;
        const chs = r.data || [];
        setChannels(chs);
        if (chs.length) setActiveChannelId(chs[0].id);
        else setActiveChannelId(null);
      })
      .catch(() => { if (live) toast.error("Failed to load chat channels"); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [taskId]);

  const loadMessages = useCallback(async () => {
    if (!activeChannelId) return;
    try {
      const r = await api.get(`/channels/${activeChannelId}/messages`);
      setMessages(r.data || []);
      setTimeout(() => listEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch {}
  }, [activeChannelId]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Poll every 5s so the chat updates without needing the WebSocket hop
  useEffect(() => {
    if (!activeChannelId) return;
    const t = setInterval(loadMessages, 5000);
    return () => clearInterval(t);
  }, [activeChannelId, loadMessages]);

  // ── @-mention autocomplete ──────────────────────────────────────────────
  const onTextChange = (e) => {
    const v = e.target.value;
    setText(v);
    if (!isInternal) return;
    const caret = e.target.selectionStart;
    // walk back to the closest '@'
    const upto = v.slice(0, caret);
    const at = upto.lastIndexOf("@");
    if (at === -1) return setMentionOpen(false);
    // '@' must be at start or preceded by whitespace
    if (at > 0 && !/\s/.test(upto[at - 1])) return setMentionOpen(false);
    const token = upto.slice(at + 1);
    if (/\s/.test(token) || token.length > 40) return setMentionOpen(false);
    setMentionAnchor(at);
    setMentionQuery(token);
    setMentionOpen(true);
  };

  useEffect(() => {
    if (!mentionOpen) return;
    const q = mentionQuery.trim();
    api.get(`/users/mentionable${q ? `?q=${encodeURIComponent(q)}` : ""}`)
      .then(r => setMentionSuggestions((r.data || []).slice(0, 6)))
      .catch(() => setMentionSuggestions([]));
  }, [mentionOpen, mentionQuery]);

  const pickMention = (u) => {
    const firstName = firstNameOf(u.name);
    const before = text.slice(0, mentionAnchor);
    const afterCaret = text.slice((textareaRef.current?.selectionStart) || text.length);
    // Visible token: "@Firstname " — clean, no markdown, no UUID.
    const token = `@${firstName} `;
    const next = before + token + afterCaret;
    setText(next);
    setPendingMentions(prev => [...prev, { firstName, fullName: u.name, userId: u.id }]);
    setMentionOpen(false);
    // Put caret right after the token
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      const pos = (before + token).length;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(pos, pos);
    });
  };

  // ── Submit ──────────────────────────────────────────────────────────────
  const submit = async () => {
    if ((!text.trim() && pendingFiles.length === 0) || !activeChannelId) return;
    setPosting(true);
    try {
      // Expand any tracked @Firstname tokens back to the strict form the
      // backend expects. This keeps the composer UX clean while preserving
      // multi-user email dispatch on the backend.
      const payloadText = expandMentions(text.trim() || "(attachment)", pendingMentions);
      const r = await api.post(`/channels/${activeChannelId}/messages`, { content: payloadText, priority });
      const messageId = r.data.id;
      // v1.56 — upload staged files against the new message.
      let failed = 0;
      for (const f of pendingFiles) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post(
            `/channels/${activeChannelId}/messages/${messageId}/attachments`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
        } catch (err) {
          failed += 1;
          toast.error(`Failed to attach ${f.name}: ${err.response?.data?.detail || err.message}`);
        }
      }
      setText("");
      setPendingMentions([]);
      setPriority("medium");
      setPendingFiles([]);
      loadMessages();
      if (failed === 0 && pendingFiles.length > 0) toast.success("Message + files sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
    setPosting(false);
  };

  // v1.56 — attachment helpers.
  const onFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPendingFiles(prev => [...prev, ...files]);
    e.target.value = "";
  };
  const removePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };
  const downloadAttachment = async (message, att) => {
    try {
      const r = await api.get(
        `/channels/${activeChannelId}/messages/${message.id}/attachments/${att.id}/download`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = att.file_name || "attachment";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  const onKey = (e) => {
    if (mentionOpen && e.key === "Escape") return setMentionOpen(false);
    if (e.key === "Enter" && !e.shiftKey && !mentionOpen) {
      e.preventDefault();
      submit();
    }
  };

  const activeChannel = useMemo(
    () => channels.find(c => c.id === activeChannelId),
    [channels, activeChannelId]
  );

  if (loading) return <div className="p-4 text-sm text-[#A3A3A3]">Loading chat…</div>;
  if (!channels.length) return <div className="p-4 text-sm text-[#A3A3A3]">No chat channel available.</div>;

  // When `fill` is set (side-panel mode) the component fills its container
  // vertically; otherwise falls back to the legacy bounded mode.
  const bodySizing = fill
    ? { flex: 1, minHeight: 0 }
    : { minHeight: 280, maxHeight: 420 };

  return (
    <div className={`flex flex-col ${fill ? "h-full" : ""}`} data-testid="task-chat-panel">
      {channels.length > 1 && (
        <div className="flex border border-[#E5E5E5] border-b-0 shrink-0">
          {channels.map(ch => (
            <button
              key={ch.id}
              onClick={() => setActiveChannelId(ch.id)}
              className={`flex-1 px-3 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${ch.id === activeChannelId ? "bg-white text-[#0055FF] border-b-[2px] border-[#0055FF] -mb-[2px]" : "bg-[#FAFAFA] text-[#525252] hover:bg-white"}`}
              data-testid={`chat-tab-${ch.channel_type}`}
            >
              {ch.channel_type === "internal" ? "Eyvo Internal" : "Client + Eyvo"}
              {ch.channel_type === "internal" && (
                <span className="ml-1 inline-block px-1 text-[9px] bg-[#FEF3C7] text-[#92400E]">staff</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="border border-[#E5E5E5] bg-white flex flex-col flex-1" style={bodySizing}>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {messages.length === 0 && (
            <p className="text-xs text-[#A3A3A3] text-center py-6">No messages yet. Start the conversation.</p>
          )}
          {messages.map(m => {
            const s = PRIORITY_STYLES[m.priority] || PRIORITY_STYLES.medium;
            return (
              <div key={m.id} className={`px-3 py-2 border border-[#E5E5E5] text-sm ${s.bubble}`} data-testid={`chat-msg-${m.id}`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-semibold text-[#0A0A0A] text-[13px]">{m.author_name || "Unknown"}</span>
                  {m.author_type === "client" && <span className="text-[9px] font-bold uppercase tracking-wider bg-[#EFF6FF] text-[#0055FF] px-1">Client</span>}
                  {m.priority !== "medium" && <span className={`text-[9px] font-bold tracking-wider px-1 ${s.chip}`}>{s.label}</span>}
                  <span className="ml-auto text-[10px] text-[#A3A3A3]">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</span>
                </div>
                <div className="whitespace-pre-wrap text-[#0A0A0A] text-[13px] leading-relaxed">{renderContent(m.content)}</div>
                {/* v1.56 — attachments rendered as clickable paperclip rows. */}
                {m.attachments && m.attachments.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid={`chat-msg-${m.id}-attachments`}>
                    {m.attachments.map(att => (
                      <button
                        key={att.id}
                        type="button"
                        onClick={() => downloadAttachment(m, att)}
                        className="w-full flex items-center gap-2 text-left px-2 py-1.5 bg-[#FAFBFF] hover:bg-[#EFF6FF] border border-[#E5E5E5] hover:border-[#0055FF] transition-colors group"
                        data-testid={`chat-download-${att.id}`}
                        title={`Download ${att.file_name}`}
                      >
                        <Paperclip size={12} className="text-[#525252] flex-shrink-0" />
                        <span className="text-[12px] text-[#0A0A0A] font-medium truncate flex-1">{att.file_name}</span>
                        <span className="text-[10px] text-[#A3A3A3] flex-shrink-0">{fmtBytes(att.file_size)}</span>
                        <DownloadSimple size={12} className="text-[#A3A3A3] group-hover:text-[#0055FF] flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div ref={listEndRef} />
        </div>

        {/* Composer */}
        <div className="border-t border-[#E5E5E5] bg-[#FAFAFA] p-2 shrink-0">
          {activeChannel?.channel_type === "shared" && !isInternal && (
            <p className="text-[10px] text-[#A3A3A3] mb-1">Messages here are visible to you and the Eyvo team.</p>
          )}
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={onTextChange}
              onKeyDown={onKey}
              placeholder={isInternal ? "Type a message. Use @ to mention a teammate…" : "Type a message…"}
              className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF] resize-none"
              rows={2}
              data-testid="chat-composer"
            />
            {mentionOpen && mentionSuggestions.length > 0 && (
              <div className="absolute left-2 bottom-full mb-1 bg-white border border-[#E5E5E5] shadow-lg min-w-[220px] z-20" data-testid="mention-dropdown">
                {mentionSuggestions.map(u => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => pickMention(u)}
                    className="w-full text-left px-3 py-1.5 hover:bg-[#EFF6FF] text-sm border-b border-[#F5F5F5] last:border-b-0"
                    data-testid={`mention-option-${u.id}`}
                  >
                    <span className="font-semibold text-[#0A0A0A]">{u.name}</span>
                    <span className="text-[#A3A3A3] text-xs ml-1">· {u.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {pendingMentions.length > 0 && (
            <div className="flex items-center gap-1 mt-1.5 flex-wrap" data-testid="pending-mentions">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Notifying:</span>
              {pendingMentions.map((m, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-1.5 py-[1px] bg-[#EFF6FF] text-[#0055FF] text-[11px] font-semibold rounded-sm">
                  @{m.firstName}
                  <button
                    type="button"
                    onClick={() => setPendingMentions(prev => prev.filter((_, j) => j !== i))}
                    className="text-[#0055FF] hover:text-[#DC2626] text-[13px] leading-none"
                    title="Remove"
                  >×</button>
                </span>
              ))}
            </div>
          )}
          {/* v1.56 — staged file preview. Both Eyvo + clients can see
              their queued uploads, remove them, and review file sizes
              before hitting Send. */}
          {pendingFiles.length > 0 && (
            <div className="space-y-1 border border-dashed border-[#BFDBFE] bg-[#EFF6FF] p-2 mt-2" data-testid="chat-staged-files">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#1E3A8A]">
                {pendingFiles.length} file{pendingFiles.length !== 1 ? "s" : ""} attached
              </div>
              {pendingFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-xs bg-white border border-[#E5E5E5] px-2 py-1">
                  <Paperclip size={11} className="text-[#525252]" />
                  <span className="flex-1 truncate">{f.name}</span>
                  <span className="text-[10px] text-[#A3A3A3]">{fmtBytes(f.size)}</span>
                  <button
                    type="button"
                    onClick={() => removePendingFile(i)}
                    className="text-[#A3A3A3] hover:text-[#DC2626]"
                    title="Remove"
                    data-testid={`chat-remove-staged-${i}`}
                  >
                    <XIcon size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 mt-2">
            {isInternal && (
              <div className="flex gap-1" data-testid="priority-picker">
                {["low", "medium", "high"].map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPriority(p)}
                    className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider border ${priority === p ? (p === "high" ? "bg-[#DC2626] text-white border-[#DC2626]" : p === "low" ? "bg-[#F5F5F5] text-[#525252] border-[#525252]" : "bg-[#0055FF] text-white border-[#0055FF]") : "bg-white text-[#525252] border-[#E5E5E5] hover:border-[#0055FF]"}`}
                    data-testid={`priority-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
            {/* v1.56 — Attach. Available to BOTH Eyvo internal users
                AND clients on shared channels (mirror of the
                comments behaviour). */}
            <label
              className="inline-flex items-center gap-1 px-2 py-1 border border-[#E5E5E5] hover:border-[#0055FF] text-[10px] font-bold uppercase tracking-wider text-[#525252] hover:text-[#0055FF] cursor-pointer transition-colors"
              data-testid="chat-attach-btn"
              title="Attach files to this message (25 MB max each)"
            >
              <Paperclip size={11} />
              Attach
              <input
                type="file"
                multiple
                onChange={onFilePick}
                className="hidden"
                data-testid="chat-attach-input"
              />
            </label>
            <button
              onClick={submit}
              disabled={(!text.trim() && pendingFiles.length === 0) || posting}
              className="ml-auto px-4 py-1.5 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50"
              data-testid="chat-send"
            >
              {posting ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
